import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Search, Building2, Lock, ShieldAlert, Download, Sparkles, Users,
  ChevronLeft, ChevronRight, Loader2, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const EMPLOYEE_RANGES = [
  { v: "1,10", l: "1–10" },
  { v: "11,50", l: "11–50" },
  { v: "51,200", l: "51–200" },
  { v: "201,500", l: "201–500" },
  { v: "501,1000", l: "501–1k" },
  { v: "1001,5000", l: "1k–5k" },
  { v: "5001,100000", l: "5k+" },
];

const splitCsv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function ApolloSearchPanel() {
  const [titles, setTitles] = useState("Logistics Director, Supply Chain Manager, Head of Procurement");
  const [personLoc, setPersonLoc] = useState("Slovenia");
  const [orgLoc, setOrgLoc] = useState("");
  const [orgDomains, setOrgDomains] = useState("");
  const [keywords, setKeywords] = useState("");
  const [ranges, setRanges] = useState([]);
  const [page, setPage] = useState(1);
  const [icpSynced, setIcpSynced] = useState(false);

  // Prefill filters from the saved ICP so the user doesn't retype them.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/icp");
        const icp = data?.icp;
        if (!icp) return;
        if ((icp.decision_maker_titles || []).length) setTitles(icp.decision_maker_titles.slice(0, 8).join(", "));
        if ((icp.geographies || []).length) setPersonLoc(icp.geographies.slice(0, 8).join(", "));
        // NOTE: we intentionally do NOT prefill the keyword from industries —
        // Apollo's People Search q_keywords is very narrow and a long multi-industry
        // string returns zero results. Leave it blank; the user can add one short term.
        setIcpSynced(true);
      } catch { /* no icp yet */ }
    })();
  }, []);

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // {people, pagination}
  const [selected, setSelected] = useState({}); // apollo_id -> person
  const [importing, setImporting] = useState(false);
  const [scoreOnImport, setScoreOnImport] = useState(true);
  const [enrichOnImport, setEnrichOnImport] = useState(true);

  const toggleRange = (v) =>
    setRanges((r) => (r.includes(v) ? r.filter((x) => x !== v) : [...r, v]));

  const runSearch = async (goPage = 1) => {
    if (!splitCsv(titles).length && !keywords.trim() && !splitCsv(orgLoc).length && !splitCsv(personLoc).length && !splitCsv(orgDomains).length) {
      toast.error("Add at least one filter (titles, locations, domains, or keywords)");
      return;
    }
    setLoading(true);
    setSelected({});
    try {
      const { data } = await api.post("/apollo/search", {
        titles: splitCsv(titles),
        person_locations: splitCsv(personLoc),
        organization_locations: splitCsv(orgLoc),
        organization_domains: splitCsv(orgDomains),
        employee_ranges: ranges,
        keywords: keywords.trim() || null,
        page: goPage,
        per_page: 25,
      });
      setResults(data);
      setPage(goPage);
      const n = data.pagination?.total_entries ?? data.people.length;
      if (!data.people?.length) {
        toast.message("No matches — try broadening filters", {
          description: "Tip: remove the keyword, use single countries (not regions like 'Benelux'), and fewer titles.",
        });
      } else {
        toast.success(`${n} matches found`);
      }
    } catch (e) {
      toast.error(`Apollo search failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setLoading(false);
  };

  const allSelected =
    results?.people?.length > 0 && results.people.every((p) => selected[p.apollo_id]);

  const toggleAll = () => {
    if (!results?.people) return;
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      results.people.forEach((p) => { next[p.apollo_id] = p; });
      setSelected(next);
    }
  };

  const toggleOne = (p) =>
    setSelected((s) => {
      const next = { ...s };
      if (next[p.apollo_id]) delete next[p.apollo_id];
      else next[p.apollo_id] = p;
      return next;
    });

  const selectedList = Object.values(selected);

  const importSelected = async () => {
    if (!selectedList.length) {
      toast.error("Select at least one lead");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/apollo/import", {
        people: selectedList,
        score: scoreOnImport,
        enrich: enrichOnImport,
      });
      const jobId = data.job_id;
      if (!jobId) throw new Error("no job id");
      toast.message("Importing…", {
        description: `Enriching & AI-drafting ${data.total} lead${data.total === 1 ? "" : "s"}. This can take a minute.`,
      });
      // Poll the background import job (avoids the gateway timeout on large batches).
      const startedAt = Date.now();
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/apollo/import/${jobId}`);
          if (job.status === "done") {
            const r = job.result || {};
            toast.success(
              `Imported ${r.imported || 0} lead${r.imported === 1 ? "" : "s"} → ${r.created || 0} new${r.enriched ? ` · ${r.enriched} enriched` : ""}${r.linkedin_resolved ? ` · ${r.linkedin_resolved} LinkedIn-ready` : ""}${r.blocked ? ` · ${r.blocked} ERP/opt-out blocked` : ""}`
            );
            setSelected({});
            setImporting(false);
            return;
          }
          if (job.status === "failed") {
            toast.error(`Import failed: ${job.error || "unknown"}`);
            setImporting(false);
            return;
          }
          if (Date.now() - startedAt > 300000) {
            toast.error("Import is taking unusually long — check Prospects in a moment.");
            setImporting(false);
            return;
          }
          setTimeout(poll, 3000);
        } catch {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(`Import failed: ${e?.response?.data?.detail || "unknown"}`);
      setImporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="apollo-panel">
      <div className="bg-signal-500/5 border border-signal-500/20 rounded-sm p-3 flex gap-3" data-testid="apollo-callout">
        <Users className="w-4 h-4 text-signal-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-foreground/80 leading-relaxed">
          <strong className="text-signal-500">Bulk net-new lead sourcing.</strong> Define your ICP (titles · geography · company size) and
          pull decision-makers from Apollo&apos;s 220M-contact database. Every result is auto-checked against your 26k ERP records — existing
          customers are flagged so the autonomous sequencer never cold-pitches them.
        </div>
      </div>

      {/* Filter form */}
      <div className="data-card space-y-4">
        <div className="label-eyebrow inline-flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-signal-500" /> ICP filters
          {icpSynced && (
            <span data-testid="apollo-icp-synced" className="text-[0.6rem] tracking-wider uppercase font-mono px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-500">
              synced from your ICP
            </span>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Job titles <span className="text-muted-foreground">(comma-separated)</span></Label>
            <Input data-testid="apollo-titles" value={titles} onChange={(e) => setTitles(e.target.value)} className="text-xs" placeholder="Logistics Director, Supply Chain Manager" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Person location</Label>
            <Input data-testid="apollo-personloc" value={personLoc} onChange={(e) => setPersonLoc(e.target.value)} className="text-xs" placeholder="Slovenia, Croatia, Germany" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Company HQ location <span className="text-muted-foreground">(optional)</span></Label>
            <Input data-testid="apollo-orgloc" value={orgLoc} onChange={(e) => setOrgLoc(e.target.value)} className="text-xs" placeholder="Austria, Italy" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Keyword <span className="text-muted-foreground">(industry / company)</span></Label>
            <Input data-testid="apollo-keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} className="text-xs" placeholder="automotive, freight forwarding" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Target companies by domain <span className="text-muted-foreground">(comma-separated — for named accounts)</span></Label>
          <Input data-testid="apollo-orgdomains" value={orgDomains} onChange={(e) => setOrgDomains(e.target.value)} className="text-xs" placeholder="logwin-logistics.com, kerrylogistics.com, noatumlogistics.com, ych.com" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Company size</Label>
          <div className="flex flex-wrap gap-1.5">
            {EMPLOYEE_RANGES.map((r) => (
              <button
                key={r.v}
                type="button"
                data-testid={`apollo-range-${r.v}`}
                onClick={() => toggleRange(r.v)}
                className={`px-2.5 py-1 rounded-sm text-[0.65rem] font-mono border transition-colors ${
                  ranges.includes(r.v)
                    ? "bg-signal-500/15 text-signal-500 border-signal-500/40"
                    : "bg-muted/20 text-muted-foreground border-border hover:border-signal-500/30"
                }`}
              >
                {r.l}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={() => runSearch(1)} disabled={loading} data-testid="apollo-search-btn" className="rounded-sm">
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
          {loading ? "Searching…" : "Search Apollo"}
        </Button>
      </div>

      {/* Results */}
      {results && (
        <div className="data-card space-y-3" data-testid="apollo-results">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              <span className="font-semibold">{results.pagination?.total_entries ?? results.people.length}</span>
              <span className="text-muted-foreground"> matches</span>
              {selectedList.length === 0 && results.people.length > 0 && (
                <span className="text-muted-foreground text-xs"> · tick rows (or the header box) to enable Import</span>
              )}
              {results.pagination?.total_pages > 1 && (
                <span className="text-muted-foreground text-xs"> · page {results.pagination.page}/{results.pagination.total_pages}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[0.65rem] text-muted-foreground inline-flex items-center gap-1.5 cursor-pointer select-none">
                <input type="checkbox" checked={enrichOnImport} onChange={(e) => setEnrichOnImport(e.target.checked)} data-testid="apollo-enrich-toggle" className="accent-signal-500" />
                <Lock className="w-3 h-3" /> Enrich (unlock email + LinkedIn)
              </label>
              <label className="text-[0.65rem] text-muted-foreground inline-flex items-center gap-1.5 cursor-pointer select-none">
                <input type="checkbox" checked={scoreOnImport} onChange={(e) => setScoreOnImport(e.target.checked)} data-testid="apollo-score-toggle" className="accent-signal-500" />
                <Sparkles className="w-3 h-3" /> AI-score
              </label>
              <Button
                onClick={importSelected}
                disabled={importing || selectedList.length === 0}
                data-testid="apollo-import-btn"
                size="sm"
                className="rounded-sm"
                title={selectedList.length === 0 ? "Tick the checkboxes on the rows you want to import first" : `Import ${selectedList.length} selected`}
              >
                {importing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                {selectedList.length === 0 ? "Select rows to import" : `Import ${selectedList.length}`}
              </Button>
            </div>
          </div>

          {results.people.length === 0 ? (
            <p className="text-xs text-muted-foreground italic py-4 text-center">No matches — broaden your filters.</p>
          ) : (
            <div className="border border-border rounded-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/30 text-[0.6rem] uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="w-8 p-2">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="apollo-select-all" className="accent-signal-500" />
                    </th>
                    <th className="text-left p-2 font-medium">Name</th>
                    <th className="text-left p-2 font-medium">Title</th>
                    <th className="text-left p-2 font-medium">Company</th>
                    <th className="text-left p-2 font-medium">ERP</th>
                  </tr>
                </thead>
                <tbody>
                  {results.people.map((p) => {
                    const isSel = !!selected[p.apollo_id];
                    const blocked = p.outreach_blocked;
                    const hasErp = p.erp_relationship?.in_contacts || p.erp_relationship?.offers_count > 0 || p.erp_relationship?.bookings_count > 0;
                    return (
                      <tr
                        key={p.apollo_id}
                        data-testid={`apollo-row-${p.apollo_id}`}
                        className={`border-t border-border hover:bg-muted/20 ${isSel ? "bg-signal-500/5" : ""}`}
                      >
                        <td className="p-2 align-top">
                          <input type="checkbox" checked={isSel} onChange={() => toggleOne(p)} data-testid={`apollo-check-${p.apollo_id}`} className="accent-signal-500" />
                        </td>
                        <td className="p-2 align-top">
                          <div className="font-medium inline-flex items-center gap-1">
                            {p.full_name}
                            {p.locked && <Lock className="w-2.5 h-2.5 text-amber-accent" title="Unlock full data with Apollo credits" />}
                          </div>
                          {p.location && <div className="text-[0.6rem] text-muted-foreground">{p.location}</div>}
                        </td>
                        <td className="p-2 align-top text-foreground/80">{p.title || "—"}</td>
                        <td className="p-2 align-top">
                          <span className="inline-flex items-center gap-1 text-signal-500">
                            <Building2 className="w-2.5 h-2.5" /> {p.company || "—"}
                          </span>
                          {p.company_size && <div className="text-[0.6rem] text-muted-foreground">{p.company_size} emp.</div>}
                        </td>
                        <td className="p-2 align-top">
                          {blocked ? (
                            <span className="inline-flex items-center gap-1 text-amber-accent text-[0.6rem]" title="Existing ERP customer — outreach blocked">
                              <ShieldAlert className="w-3 h-3" /> Blocked
                            </span>
                          ) : hasErp ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground text-[0.6rem]" title="Prior ERP relationship">
                              <AlertTriangle className="w-3 h-3" /> Known
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-500 text-[0.6rem]">
                              <CheckCircle2 className="w-3 h-3" /> New
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {results.people.some((p) => p.locked) && (
            <div className="text-[0.65rem] text-muted-foreground inline-flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> Search shows obfuscated previews. Importing with <strong className="text-signal-500">Enrich</strong> on auto-unlocks the real email + LinkedIn URL (1 Apollo credit each).
            </div>
          )}

          {results.pagination?.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-1">
              <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => runSearch(page - 1)} data-testid="apollo-prev" className="rounded-sm">
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground">page {page}</span>
              <Button variant="outline" size="sm" disabled={page >= (results.pagination.total_pages || 1) || loading} onClick={() => runSearch(page + 1)} data-testid="apollo-next" className="rounded-sm">
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
